import { useEffect, useRef, useState } from "react";
import client from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABEL, money, fmtDate, STATUS, DEFAULT_AVAILABILITY, WEEKDAYS, HOUR_OPTIONS, gradeColor } from "../lib/helpers";
import { ArrowLeft, Camera, EnvelopeSimple, Phone, Buildings, MapPin, NotePencil, CurrencyDollar, CheckCircle, Clock, House, CalendarCheck, CalendarX, X, PaperPlaneTilt, Medal, Warning, Plus } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Calendar } from "./ui/calendar";

const toYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function Card({ title, icon: Icon, children, action }) {
  return (
    <div className="border border-[#E9DDC6] rounded-md bg-white shadow-premium">
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#F0ECE2]">
        <h3 className="font-display text-lg flex items-center gap-2 text-[#2C2A28]">{Icon && <Icon size={18} weight="duotone" className="text-[#4A0000]" />}{title}</h3>
        {action}
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  return (
    <div className="border border-[#E9DDC6] rounded-md p-4 bg-[#FBFAF7]">
      <p className="eyebrow text-[#8A8782] mb-1">{label}</p>
      <p className="font-display text-2xl tabular-nums" style={{ color: tone }}>{money(value)}</p>
    </div>
  );
}

export default function MemberProfile({ userId, tiers = [], onBack, onChanged }) {
  const { user: me } = useAuth();
  const isAdmin = me.role === "admin";
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const fileRef = useRef(null);

  const [avail, setAvail] = useState(DEFAULT_AVAILABILITY);
  const [blackouts, setBlackouts] = useState([]);
  const [savingAvail, setSavingAvail] = useState(false);

  const load = () => client.get(`/users/${userId}/profile`).then((r) => {
    setData(r.data);
    const u = r.data.user;
    setForm({ name: u.name || "", role: u.role, tier: u.tier || "", phone: u.phone || "", company: u.company || "", location: u.location || "", notes: u.notes || "" });
    setAvail(u.availability || DEFAULT_AVAILABILITY);
    setBlackouts((u.blackout_dates || []).slice().sort());
  }).catch(() => toast.error("Could not load member"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  if (!data || !form) return <p className="text-[#5C5A58]" data-testid="member-profile-loading">Loading member…</p>;
  const u = data.user;
  const isPhotog = data.role === "photographer" || data.role === "dual";
  const isAgent = data.role === "agent";

  const saveAccount = async () => {
    setSaving(true);
    try {
      const payload = { email: u.email, name: form.name, role: form.role,
        tier: (form.role === "photographer" || form.role === "dual") ? (form.tier || null) : null,
        phone: form.phone, company: form.company, location: form.location, notes: form.notes };
      await client.patch(`/users/${userId}`, payload);
      toast.success("Member updated");
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const onPickFile = () => fileRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast.error("Use a PNG, JPEG or WEBP image");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5 MB");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await client.post(`/users/${userId}/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Profile picture updated");
      load(); onChanged && onChanged();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const sendReset = async () => {
    setSendingReset(true);
    try {
      const r = await client.post(`/users/${userId}/reset-link`);
      if (r.data?.reset_link) { try { await navigator.clipboard.writeText(r.data.reset_link); } catch {} }
      toast.success("Reset link sent + copied");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setSendingReset(false); }
  };

  const onBlackout = (dates) => setBlackouts((dates || []).map(toYMD).sort());
  const removeBO = (s) => setBlackouts((b) => b.filter((x) => x !== s));
  const setDay = (d, patch) => setAvail((a) => ({ ...a, [String(d)]: { ...(a[String(d)] || { enabled: true, start: "07:00", end: "19:00" }), ...patch } }));
  const saveAvailability = async () => {
    setSavingAvail(true);
    try {
      await client.patch(`/users/${userId}/referral`, { availability: avail, blackout_dates: blackouts });
      toast.success("Availability saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save availability"); }
    finally { setSavingAvail(false); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="member-profile">
      <button data-testid="member-back" onClick={onBack} className="inline-flex items-center gap-2 text-sm text-[#5C5A58] hover:text-[#4A0000] transition-colors">
        <ArrowLeft size={16} /> Back to members
      </button>

      {/* Header */}
      <div className="flex items-center gap-5 flex-wrap">
        <div className="relative group">
          <div className="w-20 h-20 rounded-full bg-[#B08D57] flex items-center justify-center text-[#4A0000] text-2xl font-bold overflow-hidden ring-2 ring-[#E9DDC6]">
            {u.avatar ? <img src={u.avatar} alt="" className="w-full h-full object-cover" /> : (u.name || u.email)[0].toUpperCase()}
          </div>
          <button data-testid="member-avatar-upload" onClick={onPickFile} disabled={uploading}
            className="absolute inset-0 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Camera size={22} weight="fill" />
          </button>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} className="hidden" data-testid="member-avatar-input" />
        </div>
        <div className="min-w-0">
          <h2 className="font-display text-3xl leading-none text-[#2C2A28]">{u.name || u.email}</h2>
          <div className="flex items-center gap-2 mt-2">
            <Badge className="bg-[#4A0000]/10 text-[#4A0000] border-[#4A0000]/20">{ROLE_LABEL[u.role]}</Badge>
            {u.tier && <Badge className="bg-[#B08D57]/15 text-[#8A6D1B] border-[#B08D57]/40">{u.tier}</Badge>}
            {u.active === false && <Badge className="bg-[#FAF9F6] text-[#5C5A58] border-[#E9DDC6]">Archived</Badge>}
          </div>
        </div>
        <div className="ml-auto">
          {isAdmin && (
            <Button data-testid="member-reset-link" variant="outline" onClick={sendReset} disabled={sendingReset} className="border-[#E9DDC6]">
              <PaperPlaneTilt size={16} className="mr-1.5" weight="duotone" />{sendingReset ? "Sending…" : "Send reset link"}
            </Button>
          )}
        </div>
      </div>

      {/* Contact & account */}
      <Card title="Contact & account" icon={EnvelopeSimple}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Full name</Label><Input data-testid="member-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white border-[#E9DDC6]" /></div>
          <div><Label>Email <span className="text-[#9A968E] text-xs font-normal">(sign-in identity)</span></Label><Input value={u.email} disabled className="bg-[#FAF9F6] border-[#E9DDC6] font-mono-num" /></div>
          <div><Label>Role</Label>
            {isAdmin ? (
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="bg-white border-[#E9DDC6]" data-testid="member-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="agent">Agent</SelectItem><SelectItem value="photographer">Photographer</SelectItem><SelectItem value="pilot">Pilot</SelectItem><SelectItem value="dual">Dual (Photo + Drone)</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
              </Select>
            ) : (
              <div data-testid="member-role-readonly" className="h-10 flex items-center px-3 rounded-md border border-[#E9DDC6] bg-[#FAF9F6] text-sm text-[#5C5A58]">{ROLE_LABEL[form.role]}</div>
            )}
          </div>
          {(form.role === "photographer" || form.role === "dual") && (
            <div><Label>Pay tier</Label>
              {isAdmin ? (
                <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
                  <SelectTrigger className="bg-white border-[#E9DDC6]" data-testid="member-tier"><SelectValue placeholder="Select tier" /></SelectTrigger>
                  <SelectContent>{tiers.map((t) => <SelectItem key={t.id} value={t.name}>{t.name} · {money(t.rate)}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <div data-testid="member-tier-readonly" className="h-10 flex items-center px-3 rounded-md border border-[#E9DDC6] bg-[#FAF9F6] text-sm text-[#5C5A58]">{form.tier || "—"}</div>
              )}
            </div>
          )}
          <div><Label className="flex items-center gap-1"><Phone size={13} weight="duotone" />Phone</Label><Input data-testid="member-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" className="bg-white border-[#E9DDC6]" /></div>
          <div><Label className="flex items-center gap-1"><Buildings size={13} weight="duotone" />Company / brokerage</Label><Input data-testid="member-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-white border-[#E9DDC6]" /></div>
          <div><Label className="flex items-center gap-1"><MapPin size={13} weight="duotone" />Location</Label><Input data-testid="member-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Charlotte, NC" className="bg-white border-[#E9DDC6]" /></div>
        </div>
        <div className="mt-4"><Label className="flex items-center gap-1"><NotePencil size={13} weight="duotone" />Internal notes <span className="text-[#9A968E] text-xs font-normal">(admin only)</span></Label>
          <Textarea data-testid="member-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="bg-white border-[#E9DDC6]" />
        </div>
        <Button data-testid="member-save" onClick={saveAccount} disabled={saving} className="mt-4 bg-[#4A0000] hover:bg-[#5C1A1A] text-white rounded-[5px] px-6">{saving ? "Saving…" : "Save changes"}</Button>
      </Card>

      {/* Money */}
      {isAgent && data.billing && (
        <Card title="Billing" icon={CurrencyDollar}>
          <div className="grid grid-cols-3 gap-4">
            <MiniStat label="Invoiced" value={data.billing.total} tone="#4A0000" />
            <MiniStat label="Collected" value={data.billing.paid} tone="#1E4620" />
            <MiniStat label="Outstanding" value={data.billing.outstanding} tone="#B08D57" />
          </div>
        </Card>
      )}
      {isPhotog && data.payouts && (
        <Card title="Payouts" icon={CurrencyDollar}>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <MiniStat label="Total" value={data.payouts.total} tone="#4A0000" />
            <MiniStat label="Paid" value={data.payouts.paid} tone="#1E4620" />
            <MiniStat label="Outstanding" value={data.payouts.unpaid} tone="#B08D57" />
          </div>
        </Card>
      )}

      {isPhotog && data.strikes && (
        <Card title="Strikes" icon={Warning}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className={`text-2xl font-display ${data.strikes.count >= 3 ? "text-[#E01E37]" : "text-[#2C2A28]"}`} data-testid="member-strike-count">{data.strikes.count}</span>
              <span className="text-sm text-[#5C5A58]">of 3 {data.strikes.count === 1 ? "strike" : "strikes"}</span>
              {data.strikes.count >= 3 && <Badge className="bg-[#E01E37]/10 text-[#E01E37] border-[#E01E37]/30">Eligible for removal</Badge>}
            </div>
            <Button size="sm" variant="outline" data-testid="add-strike-btn" className={`border-[#E9DDC6] ${isAdmin ? "" : "hidden"}`}
              onClick={async () => { const reason = window.prompt("Reason for this strike?"); if (reason === null) return; await client.post(`/photographers/${userId}/strikes`, { reason }); load(); }}>
              <Plus size={15} className="mr-1" />Add strike
            </Button>
          </div>
          {data.strikes.items.length === 0 ? (
            <p className="text-sm text-[#8A8782]">No strikes. A missed delivery deadline adds one automatically.</p>
          ) : (
            <div className="divide-y divide-[#F0ECE2]">
              {data.strikes.items.map((s) => (
                <div key={s.id} className="flex items-center justify-between py-2.5 gap-3" data-testid={`strike-${s.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{s.property_title || "Manual strike"}</p>
                    <p className="text-xs text-[#8A8782] truncate">{s.reason}{s.created_at ? ` · ${fmtDate(s.created_at)}` : ""}</p>
                  </div>
                  <button data-testid={`remove-strike-${s.id}`} title="Remove strike" className={`text-[#8A8782] hover:text-[#E01E37] shrink-0 ${isAdmin ? "" : "hidden"}`}
                    onClick={async () => { await client.delete(`/strikes/${s.id}`); load(); }}>
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
          {data.strikes.count >= 3 && data.user.active && isAdmin && (
            <Button data-testid="remove-roster-btn" className="mt-4 w-full bg-[#E01E37] hover:bg-[#c01830] text-white"
              onClick={async () => { if (!window.confirm(`Remove ${data.user.name || "this photographer"} from the roster? They will no longer be able to log in.`)) return; await client.post(`/photographers/${userId}/remove-roster`); load(); }}>
              Remove from roster
            </Button>
          )}
          {!data.user.active && <p className="mt-3 text-xs text-[#E01E37] font-medium" data-testid="member-removed-note">Removed from roster — account deactivated.</p>}
        </Card>
      )}

      {/* Shoots */}
      <Card title={`Shoots (${data.shoots.length})`} icon={House}>
        {data.shoots.length === 0 ? (
          <p className="text-sm text-[#8A8782]">No shoots yet for this member.</p>
        ) : (
          <div className="divide-y divide-[#F0ECE2]" data-testid="member-shoots">
            {data.shoots.map((s) => {
              const st = STATUS[s.status] || STATUS.scheduled;
              return (
                <div key={s.property_id} className="flex items-center justify-between py-3 gap-3" data-testid={`member-shoot-${s.property_id}`}>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{s.title}</p>
                    <p className="text-xs text-[#8A8782] truncate">{fmtDate(s.shoot_date)}{s.address ? ` · ${s.address}` : ""}{isPhotog && s.agent ? ` · ${s.agent}` : ""}{isAgent && s.photographer ? ` · ${s.photographer}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {isAgent && typeof s.amount === "number" && <span className="font-mono-num text-sm">{money(s.amount)}</span>}
                    {isPhotog && s.grade && <Badge className={`border ${gradeColor(s.grade)}`} data-testid={`member-shoot-grade-${s.property_id}`}>{s.grade}</Badge>}
                    <Badge className={st.cls}>{st.label}</Badge>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Availability (photographer) */}
      {isPhotog && (
        <Card title="Availability & days off" icon={CalendarCheck}>
          <div className="space-y-2" data-testid="member-availability">
            {WEEKDAYS.map((name, d) => {
              const day = avail[String(d)] || { enabled: false, start: "07:00", end: "19:00" };
              return (
                <div key={d} className="flex items-center gap-3 flex-wrap">
                  <Switch data-testid={`member-day-${d}`} checked={!!day.enabled} onCheckedChange={(v) => setDay(d, { enabled: v })} />
                  <span className="w-24 text-sm">{name}</span>
                  {day.enabled ? (
                    <div className="flex items-center gap-2">
                      <Select value={day.start} onValueChange={(v) => setDay(d, { start: v })}>
                        <SelectTrigger className="h-8 w-28 bg-white border-[#E9DDC6]"><SelectValue /></SelectTrigger>
                        <SelectContent>{HOUR_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <span className="text-[#8A8782] text-sm">to</span>
                      <Select value={day.end} onValueChange={(v) => setDay(d, { end: v })}>
                        <SelectTrigger className="h-8 w-28 bg-white border-[#E9DDC6]"><SelectValue /></SelectTrigger>
                        <SelectContent>{HOUR_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  ) : <span className="text-sm text-[#9A968E]">Unavailable</span>}
                </div>
              );
            })}
          </div>
          <div className="mt-6 pt-5 border-t border-[#F0ECE2]">
            <p className="text-sm font-medium flex items-center gap-2 mb-3"><CalendarX size={16} weight="duotone" className="text-[#4A0000]" />Days off</p>
            <div className="flex flex-col md:flex-row gap-5">
              <div className="rounded-lg border border-[#E9DDC6] w-fit" data-testid="member-blackout-calendar">
                <Calendar mode="multiple" selected={blackouts.map((s) => new Date(`${s}T00:00:00`))} onSelect={onBlackout} disabled={{ before: new Date() }} />
              </div>
              <div className="flex-1">
                <p className="eyebrow text-[#8A8782] mb-2">Blocked dates ({blackouts.length})</p>
                {blackouts.length === 0 ? (
                  <p className="text-sm text-[#9A968E]">No days off set.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {blackouts.map((s) => (
                      <span key={s} data-testid={`member-blackout-${s}`} className="inline-flex items-center gap-1.5 bg-[#4A0000]/5 text-[#4A0000] border border-[#4A0000]/20 rounded-[5px] pl-3 pr-2 py-1 text-sm">
                        {fmtDate(s)}
                        <button type="button" onClick={() => removeBO(s)} className="hover:bg-[#4A0000]/10 rounded-full p-0.5"><X size={12} weight="bold" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <Button data-testid="member-avail-save" onClick={saveAvailability} disabled={savingAvail} className="mt-6 bg-[#4A0000] hover:bg-[#5C1A1A] text-white rounded-[5px] px-6">{savingAvail ? "Saving…" : "Save availability"}</Button>
        </Card>
      )}
    </div>
  );
}
